import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  UserGroupIcon,
  UserIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  ChatBubbleLeftRightIcon,
  StarIcon,
  MapPinIcon,
  EyeIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';

const Collaborate = () => {
  const [activeTab, setActiveTab] = useState('teams');
  const [searchQuery, setSearchQuery] = useState('');
  const [teams, setTeams] = useState([]);
  const [soloWorkers, setSoloWorkers] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [loadingSolo, setLoadingSolo] = useState(true);
  const [errorTeams, setErrorTeams] = useState(null);
  const [errorSolo, setErrorSolo] = useState(null);
  const [hoveredUserId, setHoveredUserId] = useState(null);
  const [hoveredUserRect, setHoveredUserRect] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTeam, setNewTeam] = useState({
    name: '',
    description: '',
    skills: '',
    looking_for: '',
    location: '',
    category: '',
  });
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const navigate = useNavigate();

  // Team categories
  const categories = [
    { value: 'web_software_development', label: 'Web & Software Development' },
    { value: 'mobile_apps', label: 'Mobile Apps' },
    { value: 'ai_machine_learning', label: 'AI & Machine Learning' },
    { value: 'blockchain_crypto', label: 'Blockchain & Crypto' },
    { value: 'game_development', label: 'Game Development' },
    { value: 'ar_vr_xr', label: 'AR/VR/XR' },
    { value: 'hardware_iot', label: 'Hardware & IoT' },
    { value: 'cybersecurity', label: 'Cybersecurity' },
    { value: 'devops_infrastructure', label: 'DevOps & Infrastructure' },
    { value: 'visual_arts', label: 'Visual Arts' },
    { value: 'music_audio_projects', label: 'Music & Audio' },
    { value: 'film_video_animation', label: 'Film, Video & Animation' },
    { value: 'graphic_design_branding', label: 'Graphic Design & Branding' },
    { value: 'photography', label: 'Photography' },
    { value: 'fashion_apparel', label: 'Fashion & Apparel' },
    { value: 'performing_arts', label: 'Performing Arts' },
    { value: 'creative_writing_literature_comics', label: 'Writing & Literature' },
    { value: 'startup_mvp_development', label: 'Startup & MVP Development' },
    { value: 'ecommerce_online_stores', label: 'E-commerce & Online Stores' },
    { value: 'saas_b2b_solutions', label: 'SaaS & B2B Solutions' },
    { value: 'finance_fintech', label: 'Finance & Fintech' },
    { value: 'real_estate_proptech', label: 'Real Estate & PropTech' },
    { value: 'hr_recruitment_platforms', label: 'HR & Recruitment' },
    { value: 'marketing_advertising_projects', label: 'Marketing & Advertising' },
    { value: 'legaltech', label: 'LegalTech' },
    { value: 'nonprofits_ngos', label: 'Nonprofits & NGOs' },
    { value: 'education_learning_platforms', label: 'Education & Learning' },
    { value: 'mental_health_wellbeing', label: 'Mental Health & Wellbeing' },
    { value: 'environmental_sustainability_projects', label: 'Environmental & Sustainability' },
    { value: 'diversity_inclusion_initiatives', label: 'Diversity & Inclusion' },
    { value: 'local_community_building', label: 'Local Community Building' },
    { value: 'healthtech_medtech', label: 'HealthTech & MedTech' },
    { value: 'biotechnology_biohacking', label: 'Biotechnology & Biohacking' },
    { value: 'scientific_research_collaborations', label: 'Scientific Research' },
    { value: 'space_aerospace_projects', label: 'Space & Aerospace' },
    { value: 'university_student_projects', label: 'University & Student Projects' },
    { value: 'hackathon_ideas', label: 'Hackathon Ideas' },
    { value: 'open_source_contributions', label: 'Open Source Contributions' },
    { value: 'experimental_conceptual_projects', label: 'Experimental & Conceptual' },
    { value: 'hobby_projects', label: 'Hobby Projects' },
    { value: 'diy_maker_hardware', label: 'DIY & Maker Hardware' },
    { value: 'events_festivals', label: 'Events & Festivals' },
    { value: 'crowdfunding_campaigns', label: 'Crowdfunding Campaigns' },
    { value: 'lifestyle_productivity_tools', label: 'Lifestyle & Productivity Tools' }
  ];

  // Fetch all teams
  useEffect(() => {
    const fetchTeams = async () => {
      setLoadingTeams(true);
      setErrorTeams(null);
      try {
        const params = {};
        if (selectedCategory) {
          params.category = selectedCategory;
        }
        const res = await api.teams.getAll({ ...params, limit: 50 });
        setTeams(res.teams || res.projects || res || []); // fallback for different response shapes
      } catch (err) {
        setErrorTeams('Failed to load teams.');
      } finally {
        setLoadingTeams(false);
      }
    };
    fetchTeams();
  }, [selectedCategory]);

  // Fetch all users and filter solo workers
  useEffect(() => {
    const fetchSoloWorkers = async () => {
      setLoadingSolo(true);
      setErrorSolo(null);
      try {
        const res = await api.users.getAll({ limit: 100 });
        // Filter users who are not in any team (assuming user.teams or user.team_ids or similar)
        const users = res.users || res || [];
        const solo = users.filter(
          (u) => !u.teams || (Array.isArray(u.teams) && u.teams.length === 0) || (Array.isArray(u.team_ids) && u.team_ids.length === 0)
        );
        setSoloWorkers(solo);
      } catch (err) {
        setErrorSolo('Failed to load solo workers.');
      } finally {
        setLoadingSolo(false);
      }
    };
    fetchSoloWorkers();
  }, []);

  // Filtered lists
  const filteredTeams = teams.filter((team) =>
    team.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    team.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredSolo = soloWorkers.filter((user) =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Helper to handle mouse enter and track position
  const handleMouseEnter = (userId, e) => {
    setHoveredUserId(userId);
    const rect = e.currentTarget.getBoundingClientRect();
    setHoveredUserRect(rect);
  };
  const handleMouseLeave = () => {
    setHoveredUserId(null);
    setHoveredUserRect(null);
  };

  // Handle input change for team creation form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTeam(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle team creation
  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setCreatingTeam(true);
    try {
      // Prepare team data
      const teamData = {
        name: newTeam.name,
        description: newTeam.description,
        skills: newTeam.skills.split(',').map(skill => skill.trim()).filter(Boolean),
        looking_for: newTeam.looking_for,
        location: newTeam.location,
        category: newTeam.category,
        image_url: '',
      };
      
      await api.teams.create(teamData);
      
      // Reset form and close modal
      setNewTeam({
        name: '',
        description: '',
        skills: '',
        looking_for: '',
        location: '',
        category: '',
      });
      setShowCreateModal(false);
      
      // Refresh teams list
      const res = await api.teams.getAll({ limit: 50 });
      setTeams(res.teams || res.projects || res || []);
    } catch (err) {
      console.error('Error creating team:', err);
      alert('Failed to create team. Please try again.');
    } finally {
      setCreatingTeam(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Collaborate
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Find the perfect team or connect with talented individuals to start collaborating.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Team
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('teams')}
            className={`${
              activeTab === 'teams'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <UserGroupIcon className="h-5 w-5 mr-2" />
            Teams ({teams.length})
          </button>
          <button
            onClick={() => setActiveTab('solo')}
            className={`${
              activeTab === 'solo'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <UserIcon className="h-5 w-5 mr-2" />
            Solo Workers ({soloWorkers.length})
          </button>
        </nav>
      </div>

      {/* Search Bar */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder={`Search ${activeTab === 'teams' ? 'teams' : 'developers'}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-gray-400" />
            <select 
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50 text-gray-900 dark:text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'teams' && (
        <div className="space-y-6">
          {loadingTeams ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          ) : errorTeams ? (
            <div className="text-center py-12 text-red-600 dark:text-red-400">{errorTeams}</div>
          ) : filteredTeams.length === 0 ? (
            <div className="text-center py-12 text-gray-600 dark:text-gray-300">No teams found.</div>
          ) : (
            filteredTeams.map((team) => (
            <div
              key={team.id}
              className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex items-start space-x-4">
                {/* Team image/avatar/SVG placeholder */}
                {team.image_url || team.avatar ? (
                  <img
                    src={team.image_url || team.avatar}
                    alt={team.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : (
                  (() => {
                    // Deterministic gradient selection based on team id or name
                    const gradients = [
                      ['from-pink-200', 'to-yellow-200', 'dark:from-pink-900', 'dark:to-yellow-900'],
                      ['from-indigo-100', 'to-purple-100', 'dark:from-indigo-900', 'dark:to-purple-900'],
                      ['from-green-100', 'to-blue-100', 'dark:from-green-900', 'dark:to-blue-900'],
                      ['from-yellow-100', 'to-pink-100', 'dark:from-yellow-900', 'dark:to-pink-900'],
                      ['from-blue-100', 'to-cyan-100', 'dark:from-blue-900', 'dark:to-cyan-900'],
                      ['from-purple-100', 'to-fuchsia-100', 'dark:from-purple-900', 'dark:to-fuchsia-900'],
                    ];
                    const hash = (team.id || team.name || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
                    const grad = gradients[hash % gradients.length];
                    return (
                      <div className={`w-16 h-16 rounded-lg flex items-center justify-center bg-gradient-to-br ${grad[0]} ${grad[1]} ${grad[2]} ${grad[3]}`}> 
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 48 48"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="opacity-90"
                          aria-label="Team placeholder"
                        >
                          {/* Background circle */}
                          <circle cx="24" cy="24" r="22" fill="#c7d2fe" opacity="0.3" />
                          {/* Main person */}
                          <circle cx="24" cy="22" r="7" fill="#6366f1" />
                          <ellipse cx="24" cy="36" rx="10" ry="6" fill="#818cf8" />
                          {/* Side persons */}
                          <circle cx="12" cy="26" r="4" fill="#a5b4fc" />
                          <ellipse cx="12" cy="36" rx="5" ry="3" fill="#c7d2fe" />
                          <circle cx="36" cy="26" r="4" fill="#a5b4fc" />
                          <ellipse cx="36" cy="36" rx="5" ry="3" fill="#c7d2fe" />
                          {/* Decorative dots */}
                          <circle cx="8" cy="10" r="1" fill="#fbbf24" />
                          <circle cx="40" cy="12" r="1.2" fill="#f472b6" />
                          <circle cx="24" cy="44" r="0.8" fill="#34d399" />
                        </svg>
                      </div>
                    );
                  })()
                )}
                <div className="flex-1">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {team.name}
                      </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-2">
                        {team.description}
                      </p>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {(Array.isArray(team.skills) ? team.skills : []).map((skill) => (
                            <span
                              key={skill}
                              className="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-2 py-1 rounded-full text-xs"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                          {team.location && <span><MapPinIcon className="h-4 w-4 inline mr-1" />{team.location}</span>}
                          <span>{team.members_count || team.members || 0} members</span>
                          {team.category && (
                            <span>
                              <EyeIcon className="h-4 w-4 inline mr-1" />
                              {categories.find(cat => cat.value === team.category)?.label || team.category}
                            </span>
                          )}
                        </div>
                    </div>
                      <Link
                        to={`/teams/${team.id}`}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        View Team
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            
            ))
          )}
        </div>
      )}

      {activeTab === 'solo' && (
        <div>
          {loadingSolo ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          ) : errorSolo ? (
            <div className="text-center py-12 text-red-600 dark:text-red-400">{errorSolo}</div>
          ) : filteredSolo.length === 0 ? (
            <div className="text-center py-12 text-gray-600 dark:text-gray-300">No solo workers found.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredSolo.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  className="relative flex flex-col items-center bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 dark:from-indigo-900 dark:via-purple-900 dark:to-pink-900/80 backdrop-blur-md rounded-xl p-6 border border-gray-200 dark:border-gray-700 hover:shadow-2xl transition-shadow duration-300 min-h-[320px] group cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  onMouseEnter={e => handleMouseEnter(user.id, e)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => navigate(`/profile/${user.id}`)}
                  tabIndex={0}
                  aria-label={`View profile of ${user.name}`}
                >
                  {/* Floating bio card is now rendered via portal below */}
                  {/* Render floating bio card via portal so it's always above all tiles */}
                  {hoveredUserId && hoveredUserRect && (() => {
                    const hoveredUser = filteredSolo.find(u => u.id === hoveredUserId);
                    if (!hoveredUser || !hoveredUser.bio) return null;
                    const isDark = document.body.classList.contains('dark');
                    const style = {
                      position: 'fixed',
                      top: `${hoveredUserRect.top + hoveredUserRect.height / 2 - 60}px`,
                      left: `${hoveredUserRect.right + 12}px`,
                      zIndex: 9999,
                      width: '16rem',
                      maxWidth: '80vw',
                      pointerEvents: 'none',
                      boxShadow: '0 1px 5px 0 rgba(0, 0, 0, 0.05)',
                      borderRadius: '0.75rem',
                      padding: '0.75rem 1rem',
                      fontSize: '0.95rem',
                      fontWeight: 400,
                      lineHeight: 1.5,
                      background: isDark ? '#18181b' : '#fff',
                      color: isDark ? '#e5e7eb' : '#222',
                      animation: 'slide-in-right 0.25s cubic-bezier(0.4,0,0.2,1) both',
                    };
                    return createPortal(
                      <div style={style}>
                        <div style={{fontSize: '0.97rem', fontWeight: 400, color: 'inherit', opacity: 0.92, whiteSpace: 'pre-line'}}>{hoveredUser.bio}</div>
                      </div>,
                      document.body
                    );
                  })()}
                  <img
                    src={user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}`}
                    alt={user.name}
                    className="w-20 h-20 rounded-full object-cover mb-4 border-4 border-indigo-200 dark:border-indigo-700 shadow"
                  />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 text-center">{user.name}</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-2 text-center line-clamp-2">{user.description || user.title || ''}</p>
                  {/* Bio section */}
                  {user.bio && (
                    <div className="w-full mb-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded text-xs text-indigo-900 dark:text-indigo-200 text-center font-medium max-h-16 overflow-hidden">
                      {user.bio.length > 100 ? user.bio.slice(0, 100) + '…' : user.bio}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 justify-center mb-2">
                    {(Array.isArray(user.skills) ? user.skills : []).map((skill) => (
                      <span
                        key={skill}
                        className="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-2 py-1 rounded-full text-xs"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-4">
                    {user.location && <span><MapPinIcon className="h-4 w-4 inline mr-1" />{user.location}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create New Team</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleCreateTeam} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Team Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={newTeam.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={newTeam.description}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category *
                </label>
                <select
                  name="category"
                  value={newTeam.category}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Skills (comma separated)
                </label>
                <input
                  type="text"
                  name="skills"
                  value={newTeam.skills}
                  onChange={handleInputChange}
                  placeholder="React, Node.js, Python"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Looking For
                </label>
                <input
                  type="text"
                  name="looking_for"
                  value={newTeam.looking_for}
                  onChange={handleInputChange}
                  placeholder="Frontend Developer, Designer"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={newTeam.location}
                  onChange={handleInputChange}
                  placeholder="Remote, San Francisco, etc."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creatingTeam}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingTeam}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {creatingTeam ? 'Creating...' : 'Create Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Collaborate;